import { browserStorage, type DraftStorage } from './drafts'

/**
 * How the gowns are laid out: four to a screen, or one.
 *
 * The owner asked on 16 October for the first — "four to a screen" — with a
 * way back to the one-up feed she had asked for in September, so the grid is
 * where a phone starts and the switch at the top of the screen is the way
 * back (`docs/20-boutique-feedback-2026-09-13.md` §7).
 *
 * Remembered per phone, because it is the phone's habit and not the session's:
 * a bride who prefers the feed prefers it in every fitting room she opens, and
 * nothing about it belongs on the server or in front of a stylist. Storage that
 * is blocked, full or private costs her the memory and nothing else — every
 * read falls back to the grid and every write is allowed to fail.
 */
export type BrowseView = 'grid' | 'single'

export const DEFAULT_BROWSE_VIEW: BrowseView = 'grid'

const KEY = 'fr:browse-view'

const isView = (value: unknown): value is BrowseView => value === 'grid' || value === 'single'

export function readBrowseView(storage: DraftStorage | null = browserStorage()): BrowseView {
  try {
    const stored = storage?.getItem(KEY)
    return isView(stored) ? stored : DEFAULT_BROWSE_VIEW
  } catch {
    return DEFAULT_BROWSE_VIEW
  }
}

export function rememberBrowseView(view: BrowseView, storage: DraftStorage | null = browserStorage()): void {
  try {
    storage?.setItem(KEY, view)
  } catch {
    // Private mode, or storage switched off: the switch still works for as long
    // as the screen is open.
  }
}

/** The grid's rows, as measured once: what `firstVisibleIndex` does arithmetic on. */
export interface GridRows {
  /** Top of one row to the top of the next, gap included. */
  readonly stride: number
  /** One card, photo and caption. */
  readonly cardHeight: number
  readonly columns: number
  readonly count: number
}

/**
 * The first card that is still on the screen, in a grid that has been
 * scrolled `scrollTop` pixels.
 *
 * A row counts as gone once less than a quarter of it is showing: switching to
 * the feed should open on the gown she is looking at, not on the bottom sliver
 * of the one she has scrolled past. The answer is the first card of the first
 * row left.
 *
 * Arithmetic on the row stride rather than a walk over every card's box,
 * because it runs once a frame while she scrolls and the page is up to two
 * hundred cards. Pure, so the rule can be tested without a layout engine — the
 * grid measures and this decides.
 */
export function firstVisibleIndex(scrollTop: number, rows: GridRows): number {
  if (rows.count <= 0) return 0
  if (rows.stride <= 0 || rows.columns <= 0) return 0
  // Row r is still showing while r × stride + ¾ of a card is below the top.
  const row = Math.max(0, Math.floor((scrollTop - rows.cardHeight * 0.75) / rows.stride) + 1)
  const lastRow = Math.floor((rows.count - 1) / rows.columns)
  return Math.min(row, lastRow) * rows.columns
}
